import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { ArrowLeftIcon, ArrowPathIcon, LockClosedIcon, LockOpenIcon, MinusIcon, PlusIcon } from '@heroicons/react/24/outline';
import { DefaultService, LeagueOutput, Player, RatingsService } from '../generated-api';
import { getRatingHistories, RatingHistories } from '../services/ratingHistories';
import { useLeagues } from '../contexts/DataContext';
import { useSession } from '../contexts/SessionContext';
import { useRecentPlayers } from '../hooks/useRecentPlayers';
import PlayerSelectionStep from '../components/wizard/steps/PlayerSelectionStep';
import { comparePlayersByRank } from '../utils/playerRanking';
import { balanceTeams, RatedPlayer, splitKey, teamStrength } from '../utils/fairTeams';
import { TEAM_ANIMAL_NAMES, TEAM_COLORS, Team } from '../components/SortableEntity';
import PlayerAvatar from '../components/common/PlayerAvatar';
import CompactRatingDisplay from '../components/common/CompactRatingDisplay';

const DEFAULT_MU = 25;
const DEFAULT_SIGMA = 8.33;
const MIN_TEAMS = 2;
const MAX_TEAMS = 6;
/** Same key as useWizardState: "Start live play" hands the teams to the play wizard. */
const WIZARD_STORAGE_KEY = 'playWizardState';

interface TablePlayer extends RatedPlayer {
  sigma: number;
  gamesPlayed: number;
}

const teamName = (index: number) => TEAM_ANIMAL_NAMES[index];
// Solid team colours (same order as TEAM_COLORS), spelled out so Tailwind keeps them
const TEAM_ACCENTS = ['bg-red-500', 'bg-primary-500', 'bg-yellow-400', 'bg-stone-700', 'bg-pink-500', 'bg-purple-500'];

const FairTeamsPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { fetch: fetchLeagues } = useLeagues();
  const { playerId: currentPlayerId } = useSession();
  const { recentPlayerIds } = useRecentPlayers(20);
  const [players, setPlayers] = useState<Player[]>([]);
  const [leagues, setLeagues] = useState<LeagueOutput[]>([]);
  const [histories, setHistories] = useState<RatingHistories>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  // The finger picker's "Balance by rating" hands over its team count.
  const [teamCount, setTeamCount] = useState(() => {
    const requested = (location.state as { teamCount?: number } | null)?.teamCount;
    return requested && requested >= MIN_TEAMS && requested <= MAX_TEAMS ? requested : MIN_TEAMS;
  });
  const [teams, setTeams] = useState<string[][]>([]);
  const [locks, setLocks] = useState<Record<string, number>>({});
  const [winPercents, setWinPercents] = useState<Record<string, number>>({});

  useEffect(() => {
    (async () => {
      try {
        const [playersData, leaguesData] = await Promise.all([DefaultService.getPlayersPlayersGet(), fetchLeagues()]);
        setPlayers(playersData);
        setLeagues(leaguesData);
        const globalLeague = leaguesData.find(league => league.isGlobal);
        if (globalLeague) setHistories(await getRatingHistories(globalLeague.id));
      } catch (err) {
        console.error('Failed to load players:', err);
        setError('Failed to load players. Please try again.');
      } finally {
        setLoading(false);
      }
    })();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Like the live play wizard: you're always at the table (the wizard would add you anyway)
  useEffect(() => {
    if (currentPlayerId && players.some(p => p.id === currentPlayerId)) {
      setSelectedIds(ids => (ids.includes(currentPlayerId) ? ids : [currentPlayerId, ...ids]));
    }
  }, [currentPlayerId, players]);

  // Every player with their global rating, in leaderboard order
  const ratedPlayers = useMemo((): TablePlayer[] => {
    const rated = players.map(p => {
      const progression = histories[p.id]?.ratingProgression ?? [];
      const last = progression[progression.length - 1];
      return {
        id: p.id,
        name: p.name,
        mu: last?.ratingAfter.rating ?? DEFAULT_MU,
        sigma: last?.ratingAfter.sigma ?? DEFAULT_SIGMA,
        gamesPlayed: progression.length,
      };
    });
    return rated.sort((a, b) => comparePlayersByRank({ ...a, rating: a.mu }, { ...b, rating: b.mu }));
  }, [players, histories]);

  const imageById = useMemo(() => Object.fromEntries(players.map(p => [p.id, p.imageUrl ?? undefined])), [players]);
  const byId = useMemo(() => Object.fromEntries(ratedPlayers.map(p => [p.id, p])), [ratedPlayers]);
  const atTable = useMemo(() => ratedPlayers.filter(p => selectedIds.includes(p.id)), [ratedPlayers, selectedIds]);
  const maxTeams = Math.max(MIN_TEAMS, Math.min(MAX_TEAMS, atTable.length));
  const effectiveTeamCount = Math.min(teamCount, maxTeams);
  const canSplit = atTable.length >= MIN_TEAMS;

  // Re-balance whenever the table or the number of teams changes
  useEffect(() => {
    if (!canSplit) {
      setTeams([]);
      return;
    }
    setTeams(balanceTeams(atTable, effectiveTeamCount, { locks }));
    // Locks only apply on the next shuffle, so they don't trigger one
  }, [atTable, effectiveTeamCount, canSplit]); // eslint-disable-line react-hooks/exhaustive-deps

  // Win % per team from the API (same model as rating updates)
  useEffect(() => {
    const nonEmpty = teams.filter(t => t.length > 0);
    if (nonEmpty.length < 2) {
      setWinPercents({});
      return;
    }
    let cancelled = false;
    const request = Object.fromEntries(teams.map((ids, i) => [teamName(i), ids]).filter(([, ids]) => ids.length > 0));
    RatingsService.calculateWinProbabilitiesWinProbabilitiesCalculatePost({ requestBody: { teams: request } })
      .then(result => {
        if (!cancelled) setWinPercents(result);
      })
      .catch(err => {
        console.error('Failed to load win probabilities:', err);
        if (!cancelled) setWinPercents({});
      });
    return () => {
      cancelled = true;
    };
  }, [teams]);

  const togglePlayer = (id: string) => {
    if (id === currentPlayerId) return;
    setSelectedIds(ids => (ids.includes(id) ? ids.filter(x => x !== id) : [...ids, id]));
    setLocks(({ [id]: _, ...rest }) => rest);
  };

  const changeTeamCount = (delta: number) => {
    const next = Math.max(MIN_TEAMS, Math.min(maxTeams, effectiveTeamCount + delta));
    setTeamCount(next);
    setLocks(current => Object.fromEntries(Object.entries(current).filter(([, team]) => team < next)));
  };

  const toggleLock = (id: string, teamIndex: number) => {
    setLocks(({ [id]: current, ...rest }) => (current === undefined ? { ...rest, [id]: teamIndex } : rest));
  };

  const shuffle = () => {
    setTeams(balanceTeams(atTable, effectiveTeamCount, { locks, avoidKey: splitKey(teams) }));
  };

  // Same shape as the wizard's teams, so the picker rings each player in their team colour
  const wizardTeams: Team[] = useMemo(
    () =>
      teams.map((playerIds, i) => ({
        id: `team-fair-${i}`,
        name: teamName(i),
        color: TEAM_COLORS[i].name.toLowerCase(),
        playerIds,
      })),
    [teams],
  );

  const startLivePlay = () => {
    // The wizard picks up saved state on mount: start it at the game step with these teams
    sessionStorage.setItem(
      WIZARD_STORAGE_KEY,
      JSON.stringify({
        currentStep: 2,
        playType: 'live',
        selectedGameId: '',
        gameExtensions: [],
        selectedPlayerIds: teams.flat(),
        teams: wizardTeams,
        playDate: '',
        comment: '',
        durationMinutes: null,
        teamMetadata: {},
        selectedLeagueIds: [],
        fromLivePlayId: null,
      }),
    );
    navigate('/plays/new');
  };

  return (
    <div className="min-h-screen">
      <div className="relative pt-14 sm:pt-4 pb-4 px-4">
        <button
          onClick={() => navigate(-1)}
          className="absolute left-4 top-14 sm:top-4 p-3 -ml-3 -mt-1 text-body hover:text-heading"
          aria-label="Back"
        >
          <ArrowLeftIcon className="h-5 w-5" />
        </button>
        <h1 className="text-fluid-h2 font-bold text-heading text-center">Fair Teams</h1>
      </div>

      <div className="max-w-lg mx-auto px-4 space-y-5 pb-8">
        {error && <div className="rounded-xl bg-red-50 border border-red-200 p-3 text-sm text-red-700">{error}</div>}

        {/* Who's playing */}
        <section className="bg-surface rounded-2xl shadow-sm border border-edge p-4">
          <h2 className="font-semibold text-heading">
            Who's at the table?{' '}
            <span className="text-muted font-normal">({atTable.length} picked)</span>
          </h2>
          {loading ? (
            <p className="mt-3 text-sm text-muted">Loading players…</p>
          ) : (
            <PlayerSelectionStep
              players={players}
              selectedPlayerIds={selectedIds}
              teams={wizardTeams}
              recentPlayerIds={recentPlayerIds}
              currentPlayerId={currentPlayerId}
              leagues={leagues}
              onTogglePlayer={togglePlayer}
              onCycleTeam={() => {}}
              onNewPlayer={() => navigate('/players/new', { state: { returnPath: '/tools/fair-teams' } })}
            />
          )}
        </section>

        {/* Number of teams */}
        <section className="bg-surface rounded-2xl shadow-sm border border-edge px-4 py-3 flex items-center justify-between">
          <span className="font-semibold text-heading">Teams</span>
          <div className="flex items-center gap-3">
            <button
              onClick={() => changeTeamCount(-1)}
              disabled={effectiveTeamCount <= MIN_TEAMS}
              aria-label="Fewer teams"
              className="w-11 h-11 rounded-full border border-edge-input flex items-center justify-center text-body disabled:opacity-40"
            >
              <MinusIcon className="h-5 w-5" />
            </button>
            <span className="w-6 text-center text-lg font-bold text-heading" data-testid="team-count">
              {effectiveTeamCount}
            </span>
            <button
              onClick={() => changeTeamCount(1)}
              disabled={effectiveTeamCount >= maxTeams}
              aria-label="More teams"
              className="w-11 h-11 rounded-full border border-edge-input flex items-center justify-center text-body disabled:opacity-40"
            >
              <PlusIcon className="h-5 w-5" />
            </button>
          </div>
        </section>

        {!canSplit && !loading && (
          <p className="text-sm text-muted text-center">Pick at least two players to make teams.</p>
        )}

        {/* Teams */}
        {canSplit && teams.length > 0 && (
          <div className="space-y-4">
            {teams.map((ids, i) => {
              const color = TEAM_COLORS[i];
              const win = winPercents[teamName(i)];
              return (
                <section
                  key={teamName(i)}
                  data-testid="team-card"
                  className="bg-surface rounded-2xl shadow-sm border border-edge overflow-hidden"
                >
                  <div className={`h-1.5 ${TEAM_ACCENTS[i]}`} />
                  <div className="px-4 pt-3 pb-2 flex items-center justify-between gap-3">
                    <h3 className="flex items-center gap-2 font-serif font-semibold text-heading text-lg">
                      <span className={`w-3 h-3 rounded-full ${TEAM_ACCENTS[i]}`} aria-hidden="true" />
                      {teamName(i)}
                    </h3>
                    <div
                      className={`rounded-full px-3 py-1 text-sm font-semibold ${color.bg} ${color.text}`}
                      data-testid="win-percent"
                    >
                      {win !== undefined ? `${win.toFixed(1)}% to win` : '…'}
                    </div>
                  </div>
                  <ul className="divide-y divide-edge">
                    {ids.map(id => {
                      const p = byId[id];
                      const name = p?.name ?? '';
                      const locked = locks[id] !== undefined;
                      return (
                        <li key={id} className="flex items-center gap-3 px-4 py-2">
                          <div className="flex-shrink-0">
                            <PlayerAvatar name={name} imageUrl={imageById[id]} size="small" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="font-medium text-heading truncate">{name}</div>
                            {p && (
                              <CompactRatingDisplay
                                rating={p.mu}
                                sigma={p.sigma}
                                isUncertain={p.gamesPlayed < 5 || p.sigma > 5}
                                className="text-xs mt-0.5"
                              />
                            )}
                          </div>
                          <button
                            onClick={() => toggleLock(id, i)}
                            aria-label={locked ? `Unlock ${name}` : `Lock ${name} in ${teamName(i)}`}
                            aria-pressed={locked}
                            className={`w-11 h-11 -mr-2 flex-shrink-0 flex items-center justify-center rounded-full ${
                              locked ? 'text-primary-700 bg-primary-50' : 'text-disabled hover:text-body'
                            }`}
                          >
                            {locked ? <LockClosedIcon className="h-5 w-5" /> : <LockOpenIcon className="h-5 w-5" />}
                          </button>
                        </li>
                      );
                    })}
                  </ul>
                  <div className="px-4 py-2 text-xs text-muted border-t border-edge">
                    Team strength{' '}
                    <span className="font-mono text-body">μ {teamStrength(ids.map(id => byId[id]?.mu ?? DEFAULT_MU)).toFixed(2)}</span>
                  </div>
                </section>
              );
            })}
          </div>
        )}

        {canSplit && (
          <div className="space-y-3">
            <button
              data-track="fair_teams_shuffle"
              onClick={shuffle}
              className="w-full min-h-[48px] rounded-xl border border-primary-700 text-primary-700 font-semibold flex items-center justify-center gap-2 hover:bg-primary-50"
            >
              <ArrowPathIcon className="h-5 w-5" />
              Shuffle again
            </button>
            <button
              data-track="fair_teams_start_live_play"
              onClick={startLivePlay}
              className="w-full min-h-[48px] rounded-xl bg-primary-700 text-white font-semibold hover:bg-primary-800"
            >
              Start live play
            </button>
          </div>
        )}

        <p className="text-xs text-muted">
          Balanced on global rating (mu): a team counts as its best player +10% per extra teammate. Tap the lock to keep a
          player in their team when you shuffle.
        </p>
      </div>
    </div>
  );
};

export default FairTeamsPage;
